import React, { createContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { toast } from 'react-hot-toast';
import { getAuthErrorMessage } from '@/hooks/useAuth';
import ProfileSetup from '@/components/ProfileSetup';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ user: User; session: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  const [tempUser, setTempUser] = useState<User | null>(null);

  useEffect(() => {
    // Check current session and set up auth state listener
    const initAuth = async () => {
      try {
        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setSession(session);
          
          // Check if user has completed profile setup
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, date_of_birth')
            .eq('user_id', session.user.id)
            .single();

          if (profile?.first_name && profile?.last_name && profile?.date_of_birth) {
            setUser(session.user);
          } else {
            setTempUser(session.user);
            setNeedsProfileSetup(true);
          }
        }

        // Set up auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (session?.user) {
            setSession(session);
            
            // Check profile completion on auth state change
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name, date_of_birth')
              .eq('user_id', session.user.id)
              .single();

            if (profile?.first_name && profile?.last_name && profile?.date_of_birth) {
              setUser(session.user);
              setNeedsProfileSetup(false);
              setTempUser(null);
            } else {
              setTempUser(session.user);
              setNeedsProfileSetup(true);
              setUser(null);
            }
          } else {
            setUser(null);
            setSession(null);
            setNeedsProfileSetup(false);
            setTempUser(null);
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setSession(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (!data?.user) {
        throw new Error('Sign in failed. Please try again.');
      }

      // Check if profile is complete
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, date_of_birth')
        .eq('user_id', data.user.id)
        .single();

      if (profile?.first_name && profile?.last_name && profile?.date_of_birth) {
        setUser(data.user);
        toast.success('Welcome back!');
      } else {
        setTempUser(data.user);
        setNeedsProfileSetup(true);
      }
    } catch (error: any) {
      const message = getAuthErrorMessage(error);
      toast.error(message);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      if (!data?.user) {
        throw new Error('Sign up failed. Please try again.');
      }

      setTempUser(data.user);
      setNeedsProfileSetup(true);
      return data;
    } catch (error: any) {
      const message = getAuthErrorMessage(error);
      toast.error(message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setNeedsProfileSetup(false);
      setTempUser(null);
      toast.success('Signed out successfully');
    } catch (error: any) {
      const message = getAuthErrorMessage(error);
      toast.error(message);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success('Password reset instructions sent to your email');
    } catch (error: any) {
      const message = getAuthErrorMessage(error);
      toast.error(message);
      throw error;
    }
  };

  const handleProfileSetupComplete = () => {
    if (tempUser) {
      setUser(tempUser);
      setNeedsProfileSetup(false);
      setTempUser(null);
      toast.success('Profile setup complete!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (needsProfileSetup && tempUser) {
    return (
      <ProfileSetup
        user={tempUser}
        onComplete={handleProfileSetupComplete}
        onCancel={async () => {
          await signOut();
        }}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ user, session, signIn, signUp, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}